import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import InfoIcon from "@mui/icons-material/Info";
import SettingsIcon from "@mui/icons-material/Settings";
import {Box, CircularProgress, Grid, IconButton, Tooltip, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {v4 as uuidv4} from "uuid";
import Chart from "../../components/chart/Chart";
import CustomEmpty from "../../components/custom-empty";
import FullscreenProvider, {useFullscreen} from "../../hocs/fullscreen-provider";
import ChartZoomIn from "../chart-zoom/ChartZoomIn";
import ChartZoomOut from "../chart-zoom/ChartZoomOut";
import ChartZoomReset from "../chart-zoom/ChartZoomReset";
import {FILTER_DIM_SEPARATOR} from "./constants";
import {DIMENSION_TYPE} from "./constants";
import Filter from "./Filter";
import SettingsDialog from "./SettingsDialog";
import {appConfigSelector} from "../../state/app-config/appConfigSelectors";
import {languageSelector} from "../../state/app/appSelectors";
import {hubNodesSelector} from "../../state/hub/hubSelectors";
import {nodeSelector} from "../../state/node/nodeSelectors";
import EXTRAS from "../../utils/extras";
import {getChartData} from "./utils";

function getFilterValues(data, filterDims) {
  const seen = new Set();
  const result = [];

  for (const item of data) {
    const filterValue = filterDims.reduce((acc, dim) => {
      acc[dim] = item[dim];
      return acc;
    }, {});
    const filterId = filterDims.map(dim => String(filterValue[dim])).join(FILTER_DIM_SEPARATOR);
    if (!seen.has(filterId)) {
      seen.add(filterId);
      result.push({
        id: filterId,
        value: filterValue
      });
    }
  }

  return result;
}

const ViewerChartComponent = ({
  type,
  dimensions,
  measures,
  categoryDim: externalPrimaryDim,
  secondaryDim: externalSecondaryDim,
  data,
  initialSettings,
  title,
  nodeId,
  showTitle = false,
  infoMessage,
  disableWheelZoom = false,
  disableSettings = false,
  disableFullscreen = false,
  enableAutoSecondaryDim = false,
  leftActions,
  rightActions,
  onConfigChange
}) => {
  const {t} = useTranslation();

  const {isFullscreen, handleFullscreen} = useFullscreen();

  const language = useSelector(languageSelector);

  const nodes = useSelector(hubNodesSelector);
  const node = useSelector(nodeSelector);
  const selectedNode = nodeId ? nodes?.find(node => node.nodeId === nodeId) : node;

  const chartId = useMemo(() => uuidv4(), []);

  const chartDefaultSettings = useSelector(appConfigSelector)?.chartDefaultSettings;

  const [isErrorVisible, setErrorVisibility] = useState(false);

  const [primaryDim, setPrimaryDim] = useState(null);
  const [secondaryDim, setSecondaryDim] = useState(null);
  const [filterDims, setFilterDims] = useState(null);
  const [valueDims, setValueDims] = useState(null);

  const [filter, setFilter] = useState(null);
  const [filterValues, setFilterValues] = useState(null);

  const [chartSettings, setChartSettings] = useState({
    ...(chartDefaultSettings ?? {}),
    ...(initialSettings ?? {})
  });

  const [isSettingsVisible, setIsSettingsVisible] = useState(false);

  const chartRef = useRef();

  const onChartSettingsChange = settings => {
    if (onConfigChange) onConfigChange({chartSettings: settings});
  };

  const labelMap = useMemo(
    () =>
      [...(dimensions ?? []), ...(measures ?? [])].reduce((acc, {id, title}) => {
        acc[id] = title;
        return acc;
      }, {}),
    [dimensions, measures]
  );

  const dimensionTypeMap = useMemo(
    () => Object.fromEntries((dimensions ?? []).map(({id, type}) => [id, type])),
    [dimensions]
  );

  const timeFormatMap = useMemo(() => {
    const parsedExtras = EXTRAS.parse(selectedNode?.extras);

    return {
      [DIMENSION_TYPE.dateTime]: parsedExtras?.TimePeriodFormatDate?.[language],
      [DIMENSION_TYPE.time]: parsedExtras?.TimePeriodFormatTime?.[language],
      [DIMENSION_TYPE.day]: parsedExtras?.TimePeriodFormatDay?.[language],
      [DIMENSION_TYPE.month]: parsedExtras?.TimePeriodFormatMonth?.[language],
      [DIMENSION_TYPE.year]: parsedExtras?.TimePeriodFormatYear?.[language],
      [DIMENSION_TYPE.quarter]: parsedExtras?.TimePeriodFormatQuarter?.[language],
      [DIMENSION_TYPE.quadrimester]: parsedExtras?.TimePeriodFormatQuadrimester?.[language],
      [DIMENSION_TYPE.semester]: parsedExtras?.TimePeriodFormatSemester?.[language]
    };
  }, [selectedNode, language]);

  const dimensionValuesMap = useMemo(() => {
    if (!dimensions || !data) return {};
    const sets = Object.fromEntries(dimensions.map(({id}) => [id, new Set()]));
    data.forEach(row => {
      dimensions.forEach(({id}) => {
        if (row[id]) {
          sets[id].add(row[id]);
        }
      });
    });
    return Object.fromEntries(Object.entries(sets).map(([id, set]) => [id, Array.from(set)]));
  }, [dimensions, data]);

  useEffect(() => {
    const dimensionIds = (dimensions ?? []).map(({id}) => id);
    const measureIds = (measures ?? []).map(({id}) => id);

    if (dimensionIds.length === 0 || measureIds.length === 0) {
      setErrorVisibility(true);
    } else if ((data ?? []).length > 0) {
      setErrorVisibility(false);

      const primaryDim =
        externalPrimaryDim && dimensionIds.includes(externalPrimaryDim) ? externalPrimaryDim : dimensionIds[0];

      const secondaryDim =
        externalSecondaryDim && dimensionIds.includes(externalSecondaryDim)
          ? externalSecondaryDim
          : enableAutoSecondaryDim
            ? [...dimensionIds]
                .filter(dim => dim !== primaryDim)
                .sort((dim1, dim2) => dimensionValuesMap[dim1].length - dimensionValuesMap[dim2].length)
                .find(dim => measureIds.length * dimensionValuesMap[dim].length <= 10) || null
            : null;

      const filterDims = dimensionIds.filter(dim => dim !== primaryDim && dim !== secondaryDim);

      setPrimaryDim(primaryDim);
      setSecondaryDim(secondaryDim);
      setFilterDims(filterDims);
      setValueDims(measureIds);

      if (filterDims.length > 0) {
        const filterValues = getFilterValues(data, filterDims);
        setFilter(filterValues[0].id);
        setFilterValues(filterValues);
      } else {
        setFilter(null);
        setFilterValues(null);
      }
    }
  }, [
    dimensions,
    measures,
    externalPrimaryDim,
    externalSecondaryDim,
    enableAutoSecondaryDim,
    data,
    dimensionValuesMap
  ]);

  const chartData = useMemo(() => {
    if (primaryDim && valueDims) {
      return getChartData(
        type,
        data,
        primaryDim,
        secondaryDim,
        valueDims,
        filterDims,
        filter,
        dimensionValuesMap,
        labelMap,
        dimensionTypeMap,
        timeFormatMap
      );
    }
    return null;
  }, [
    type,
    data,
    primaryDim,
    secondaryDim,
    filterDims,
    valueDims,
    filter,
    dimensionValuesMap,
    labelMap,
    dimensionTypeMap,
    timeFormatMap
  ]);

  const chartZoomReset = useCallback(() => {
    if (chartRef && chartRef.current) chartRef.current.resetZoom();
  }, [chartRef]);

  const chartZoomIn = useCallback(() => {
    if (chartRef && chartRef.current) chartRef.current.zoomIn();
  }, [chartRef]);

  const chartZoomOut = useCallback(() => {
    if (chartRef && chartRef.current) chartRef.current.zoomOut();
  }, [chartRef]);

  return isErrorVisible ? (
    <CustomEmpty />
  ) : chartData === null ? (
    <CustomEmpty text={t("components.chart.loading") + "..."} image={<CircularProgress />} />
  ) : (
    <Box
      id="viewer-chart"
      className="viewer-chart"
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "8px 16px 16px"
      }}
    >
      <Grid container justifyContent="space-between" alignItems="flex-start" className="viewer-chart__header">
        <Grid className="viewer-chart__header__title" size="grow">
          {showTitle && (title || "").length > 0 ? (
            <Typography
              variant="h6"
              className="viewer-chart__header__title__text"
              sx={{display: "inline", verticalAlign: "middle"}}
            >
              {title}
            </Typography>
          ) : (
            <Box sx={{padding: "0", marginLeft: "8px"}}>{leftActions}</Box>
          )}
        </Grid>
        <Grid
          display="flex"
          alignItems="center"
          wrap="nowrap"
          columnSpacing={1}
          className="viewer-chart__header__actions"
          sx={{marginRight: "8px"}}
        >
          {(infoMessage || "").length > 0 && (
            <Grid className="viewer-chart__header__actions__info-message">
              <Tooltip title={infoMessage} placement="bottom">
                <InfoIcon
                  sx={{
                    color: "rgba(0, 0, 0, 0.54)",
                    display: "inline",
                    verticalAlign: "middle",
                    margin: "8px"
                  }}
                />
              </Tooltip>
            </Grid>
          )}
          <Grid id="dataset-zoom-reset-container">
            <ChartZoomReset id="viewer-chart__header__actions__zoom-reset" color="action" onClick={chartZoomReset} />
          </Grid>
          <Grid id="dataset-zoom-out-container">
            <ChartZoomOut id="viewer-chart__header__actions__zoom-out" color="action" onClick={chartZoomOut} />
          </Grid>
          <Grid id="dataset-zoom-in-container">
            <ChartZoomIn id="viewer-chart__header__actions__zoom-in" color="action" onClick={chartZoomIn} />
          </Grid>
          {rightActions}
          {!disableSettings && (
            <Grid className={"viewer-chart__header__actions__settings"}>
              <IconButton
                aria-label={t("modules.viewerChart.actions.settings.tooltip")}
                onClick={() => setIsSettingsVisible(true)}
              >
                <Tooltip title={t("modules.viewerChart.actions.settings.tooltip")}>
                  <SettingsIcon />
                </Tooltip>
              </IconButton>
              <SettingsDialog
                isSettingsDialogVisible={isSettingsVisible}
                setIsSettingsDialogVisible={setIsSettingsVisible}
                chartSettings={chartSettings ?? chartDefaultSettings}
                setChartSettings={setChartSettings}
                onChartSettingsChange={onChartSettingsChange}
              />
            </Grid>
          )}
          {!disableFullscreen && (
            <Grid className="viewer-chart__header__actions__fullscreen-btn">
              <Tooltip
                title={
                  isFullscreen
                    ? t("modules.viewerChart.actions.fullscreenBtn.exit")
                    : t("modules.viewerChart.actions.fullscreenBtn.enter")
                }
                placement="bottom"
              >
                <IconButton onClick={handleFullscreen}>
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      </Grid>

      {filter && (filterValues ?? []).length > 0 && (
        <Box
          id="viewer-chart__filter"
          sx={{
            width: "100%"
          }}
        >
          <Filter
            value={filter}
            values={filterValues}
            setValue={setFilter}
            filterDims={filterDims}
            dimensionLabelMap={labelMap}
            dimensionTypeMap={dimensionTypeMap}
            timeFormatMap={timeFormatMap}
          />
        </Box>
      )}
      <Box
        id="viewer-chart__chart"
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          flex: 1
        }}
      >
        <Chart
          ref={chartRef}
          chartId={chartId}
          type={type}
          chartData={chartData}
          chartSettings={chartSettings ?? chartDefaultSettings}
          disableWheelZoom={disableWheelZoom}
        />
      </Box>
    </Box>
  );
};

const ViewerChart = props => (
  <FullscreenProvider>
    <ViewerChartComponent {...props} />
  </FullscreenProvider>
);

export default ViewerChart;
